import React from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { translations } from '@/locale';
import { OrganizationMemberAdd } from '@/domains';
import { inviteVendorMember } from '@/services/api/vendors';

import { InstanceProps } from '@/components/Modal';
import Typography from '@/components/Typography';
import { Role } from '@/constants';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const fields = translations.inputs;
const texts = translations.modals.addMember;

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  role: z.enum([Role.VendorAdmin, Role.Cashier], {
    required_error: 'Role is required'
  }),
  email: z.string().email('Invalid email address')
});

type FormValues = z.infer<typeof formSchema>;

export interface Props extends InstanceProps {
  vendorId: string;
  onMemberInvited: () => any;
}

const InviteVendorMemberModal: React.FC<Props & WrappedComponentProps> = ({
  close,
  vendorId,
  onMemberInvited,
  intl
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      role: Role.Cashier,
      email: ''
    }
  });

  async function onSubmit(values: FormValues) {
    try {
      await inviteVendorMember(vendorId, values as OrganizationMemberAdd);
      close();
      onMemberInvited();
    } catch (error) {
      console.error('Failed to invite member:', error);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Typography is="span" className="font-bold">
          <FormattedMessage id={texts.title} />
        </Typography>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {intl.formatMessage({ id: fields.name.label })}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder={intl.formatMessage({ id: fields.name.placeholder })} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {intl.formatMessage({ id: fields.role.label })}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={intl.formatMessage({ id: fields.role.placeholder })} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {[Role.VendorAdmin, Role.Cashier].map((role) => (
                      <SelectItem key={role} value={role}>
                        <FormattedMessage id={translations.roles[role]} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {intl.formatMessage({ id: fields.email.label })}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder={intl.formatMessage({ id: fields.email.placeholder })} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={!form.formState.isDirty || form.formState.isSubmitting}>
            <FormattedMessage id={texts.confirm} />
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default injectIntl(InviteVendorMemberModal);
